import { useState } from 'react';
import { useStore } from '@/infra/store';
import { useT } from '@/infra/i18n';
import { dispatch, listDispatchableNpcs } from '@/services/afk-expedition';
import type { AfkDuration } from '@/domain/world/afk';
import type { BiomeId } from '@/domain/world/types';
import { ALL_BIOMES } from '@/domain/world/types';
import { COLORS, SPACING, RADIUS, FONT } from '@/presentation/theme/tokens';
import { overlayStyle, cardStyle } from '@/presentation/modals/modal-shared';

interface Props {
  open: boolean;
  onClose: () => void;
}

const DURATIONS: AfkDuration[] = ['short', 'medium', 'long'];

export function AfkDispatchModal({ open, onClose }: Props) {
  const t = useT();
  const npcs = useStore((s) => s.npcs);
  const [npcId, setNpcId] = useState<string | null>(null);
  const [biome, setBiome] = useState<BiomeId>('meadow');
  const [duration, setDuration] = useState<AfkDuration>('short');

  if (!open) return null;
  const dispatchableIds = new Set(listDispatchableNpcs());
  const availableNpcs = npcs.filter((n) => dispatchableIds.has(n.id));

  const onSubmit = (): void => {
    if (!npcId) return;
    const r = dispatch({ npcId, biome, duration });
    if (r) onClose();
  };

  return (
    <div role="dialog" data-testid="afk-dispatch-modal" style={overlayStyle} onClick={onClose}>
      <div style={{ ...cardStyle, maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={titleStyle}>{t('afk.dispatch.title')}</h2>

        <label style={labelStyle}>{t('afk.dispatch.npc')}</label>
        <select
          data-testid="afk-npc-select"
          value={npcId ?? ''}
          onChange={(e) => setNpcId(e.target.value || null)}
          style={selectStyle}
        >
          <option value="">—</option>
          {availableNpcs.map((n) => (
            <option key={n.id} value={n.id}>
              {n.name}
            </option>
          ))}
        </select>

        <label style={labelStyle}>{t('afk.dispatch.biome')}</label>
        <div style={chipRow}>
          {ALL_BIOMES.map((b) => (
            <button
              key={b}
              data-testid={`afk-biome-${b}`}
              onClick={() => setBiome(b)}
              style={chipStyle(biome === b)}
            >
              {t(`biome.${b}`)}
            </button>
          ))}
        </div>

        <label style={labelStyle}>{t('afk.dispatch.duration')}</label>
        <div style={chipRow}>
          {DURATIONS.map((d) => (
            <button
              key={d}
              data-testid={`afk-duration-${d}`}
              onClick={() => setDuration(d)}
              style={chipStyle(duration === d)}
            >
              {t(`afk.dispatch.${d}`)}
            </button>
          ))}
        </div>

        <div style={btnRow}>
          <button onClick={onClose} style={cancelBtn}>
            {t('afk.dispatch.cancel')}
          </button>
          <button
            data-testid="afk-submit"
            disabled={!npcId}
            onClick={onSubmit}
            style={{ ...submitBtn, opacity: npcId ? 1 : 0.5 }}
          >
            {t('afk.dispatch.start')}
          </button>
        </div>
      </div>
    </div>
  );
}

const titleStyle: React.CSSProperties = {
  margin: `0 0 ${SPACING.md}px 0`,
  fontSize: FONT.sizeLg,
  color: COLORS.accent,
  textAlign: 'center',
};
const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: FONT.mono,
  fontSize: FONT.sizeXs,
  color: COLORS.textDim,
  marginTop: SPACING.md,
  marginBottom: SPACING.xs,
  textTransform: 'uppercase',
  letterSpacing: 1,
};
const selectStyle: React.CSSProperties = {
  width: '100%',
  background: COLORS.surfaceAlt,
  color: COLORS.text,
  border: `1px solid ${COLORS.border}`,
  borderRadius: RADIUS.sm,
  padding: SPACING.sm,
  fontSize: FONT.sizeSm,
  fontFamily: FONT.family,
};
const chipRow: React.CSSProperties = {
  display: 'flex',
  gap: SPACING.xs,
  flexWrap: 'wrap',
};
function chipStyle(active: boolean): React.CSSProperties {
  return {
    background: active ? COLORS.accent : COLORS.surfaceAlt,
    color: active ? '#000' : COLORS.text,
    border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
    borderRadius: RADIUS.sm,
    padding: '6px 10px',
    cursor: 'pointer',
    fontSize: FONT.sizeXs,
    fontFamily: FONT.mono,
  };
}
const btnRow: React.CSSProperties = {
  display: 'flex',
  gap: SPACING.sm,
  marginTop: SPACING.lg,
};
const cancelBtn: React.CSSProperties = {
  flex: 1,
  background: 'transparent',
  color: COLORS.textDim,
  border: `1px solid ${COLORS.border}`,
  borderRadius: RADIUS.md,
  padding: '8px 14px',
  cursor: 'pointer',
  fontSize: FONT.sizeSm,
};
const submitBtn: React.CSSProperties = {
  flex: 1,
  background: COLORS.accent,
  color: '#000',
  border: 'none',
  borderRadius: RADIUS.md,
  padding: '8px 14px',
  cursor: 'pointer',
  fontSize: FONT.sizeSm,
  fontWeight: 600,
};
